import { brand } from '@lib/config/brand';

export type FooterLink = {
  label: string;
  featureFlag?: string;
} & (
  | { href: string; action?: never }
  | { action: 'open-cookie-settings'; href?: never }
);

export type FooterHrefLink = Extract<FooterLink, { href: string }>;

const { gphcReg } = brand;

export const FOOTER_HELP_LINKS: FooterLink[] = [
  { label: 'How It Works', href: '/how-it-works' },
  { label: 'FAQs', href: '/faqs' },
  { label: 'Contact Us', href: '/contact' },
  { label: 'Delivery Information', href: '/shipping' },
  {
    // Deliberately not a literal `mailto:` href — see
    // use-footer-email-listener.ts for why. This sentinel href is
    // intercepted client-side and swapped for the real mailto: link.
    // Known trade-off: before hydration completes, clicking this link
    // is a no-op (browser just appends the `#open-email` fragment) —
    // accepted in exchange for avoiding Cloudflare Scrape Shield's
    // mailto: rewrite, which added ~646ms via an injected decode script.
    label: 'Email us',
    href: brand.email ? '#open-email' : '/contact',
  },
];

export const FOOTER_LEGAL_LINKS: FooterLink[] = [
  { label: 'Manage Cookies', action: 'open-cookie-settings' },
  { label: 'Returns Policy', href: '/shipping' },
];

export const FOOTER_BOTTOM_LINKS: FooterHrefLink[] = [
  { label: 'Privacy Notice', href: '/privacy' },
  { label: 'Terms & Conditions', href: '/terms' },
  { label: 'Cookie Policy', href: '/cookie-policy' },
  { label: 'Complaints', href: '/complaints' },
  { label: 'Terms of Sale', href: '/terms-of-sale' },
  { label: 'Sitemap', href: '/sitemap.xml' },
];

export const NEWSLETTER = {
  label: 'Email address',
  title: 'Stay Up To Date',
  buttonLabel: 'Subscribe',
  buttonIcon: 'arrowRight',
  subText:
    'Yes, send me personalised offers, vouchers, latest deals, health advice, product launches and more.',
  placeholder: 'Email Address *',
  successMessage: 'Thank you for subscribing!',
  requiredErrorMessage: 'Please enter a valid email address.',
  invalidEmailErrorMessage: 'Please enter a valid email address.',
  termsHref: '/terms',
};

const ACCREDITATION_LOGO_IMAGE_BASE = '/trust-badges';
const ACCREDITATION_LOGO_DISPLAY_SIZES = {
  legitScript: { width: 55, height: 60 },
  cyberEssentials: { width: 50, height: 60 },
  gphc: { width: 148, height: 41 },
};

export const PAYMENT_METHODS = [
  { icon: 'paymentVisa', label: 'Visa' },
  { icon: 'paymentMastercard', label: 'Mastercard' },
  { icon: 'paymentMaestro', label: 'Maestro' },
  { icon: 'paymentAmex', label: 'American Express' },
  { icon: 'paymentPaypal', label: 'PayPal' },
];

export const ACCREDITATION_LOGOS = {
  logos: [
    {
      name: 'LegitScript Certified',
      logo: {
        id: 'accreditation-legitscript',
        url: `${ACCREDITATION_LOGO_IMAGE_BASE}/legitscript.png`,
        alt: 'LegitScript Certified',
        width: ACCREDITATION_LOGO_DISPLAY_SIZES.legitScript.width,
        height: ACCREDITATION_LOGO_DISPLAY_SIZES.legitScript.height,
      },
      link: { href: 'https://www.legitscript.com/' },
    },
    {
      name: 'Cyber Essentials Certified Plus',
      logo: {
        id: 'accreditation-cyber-essentials',
        url: `${ACCREDITATION_LOGO_IMAGE_BASE}/cyber-essentials.png`,
        alt: 'Cyber Essentials Certified Plus',
        width: ACCREDITATION_LOGO_DISPLAY_SIZES.cyberEssentials.width,
        height: ACCREDITATION_LOGO_DISPLAY_SIZES.cyberEssentials.height,
      },
      link: { href: 'https://www.ncsc.gov.uk/cyberessentials/overview' },
    },
    {
      name: 'General Pharmaceutical Council',
      logo: {
        id: 'accreditation-gphc',
        url: `${ACCREDITATION_LOGO_IMAGE_BASE}/gphc-badge.png`,
        alt: `General Pharmaceutical Council registration ${gphcReg}`,
        width: ACCREDITATION_LOGO_DISPLAY_SIZES.gphc.width,
        height: ACCREDITATION_LOGO_DISPLAY_SIZES.gphc.height,
      },
      link: {
        href: `https://www.pharmacyregulation.org/registers/pharmacy/${gphcReg}`,
      },
    },
  ],
};
